import React from 'react';
import { formatNumber } from 'libphonenumber-js';
import Modal from '@rental-manager/pages/Onboarding/WizardShell/WizardScreen';
import { months } from '@common/paymentsUtils/dateUtils';
import { validateAllInputs } from '../validation';
import userPersonas from '@common/constants/userPersonas';
import errorMessages from '@common/constants/errorMessages';
import {
  Box,
  Button,
  FieldSet,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  Legend,
  Paragraph,
  Select,
  TextButton,
} from '@zillow/constellation-10';
import UnitedStatesSelect from '@common/components/UnitedStatesSelect';
import MaskedInput from '@common/components/ConstellationMaskedInput';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

type Props = {
  companyName: string;
  companyDbaName: string;
  companyTaxId: string;
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phoneNumber: string;
  verifiedPhoneNumber: string;
  dobMonth: string;
  dobDay: string;
  dobYear: string;
  ssn: string;
  editMode: boolean;
  companyAddress: string;
  companyCity: string;
  companyState: string;
  companyZip: string;
  onSubmit: (identityVerificationInfo: { identityVerificationInfo: Record<string, unknown> }) => Promise<unknown>;
  onBack: (info: Record<string, unknown>) => void;
  onCancel: () => void;
  onClose: () => void;
  selectedOption: string;
  emergencyMode: boolean;
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
};

const TaxInfoInputPage: React.FC<React.PropsWithChildren<Props>> = (props: Props): JSX.Element => {
  const initialIdVerificationInfo = {
    companyName: { value: props.companyName ?? '', error: '' },
    companyDbaName: { value: props.companyDbaName ?? '', error: '' },
    companyTaxId: { value: props.companyTaxId ?? '', error: '' },
    firstName: { value: props.firstName ?? '', error: '' },
    lastName: { value: props.lastName ?? '', error: '' },
    address: { value: props.address ?? '', error: '' },
    city: { value: props.city ?? '', error: '' },
    state: { value: props.state ?? '', error: '' },
    zip: { value: props.zip ?? '', error: '' },
    phoneNumber: { value: props.phoneNumber || props.verifiedPhoneNumber || '', error: '' },
    dobMonth: { value: props.dobMonth ?? '', error: '' },
    dobDay: { value: props.dobDay ?? '', error: '' },
    dobYear: { value: props.dobYear ?? '', error: '' },
    ssn: { value: props.ssn ?? '', error: '' },
    companyAddress: { value: props.companyAddress ?? '', error: '' },
    companyCity: { value: props.companyCity ?? '', error: '' },
    companyState: { value: props.companyState ?? '', error: '' },
    companyZip: { value: props.companyZip ?? '', error: '' },
  };

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [idVerificationInfo, setIdVerificationInfo] = React.useState<Record<string, any>>(initialIdVerificationInfo);

  const {
    companyName,
    companyDbaName,
    companyTaxId,
    firstName,
    lastName,
    address,
    city,
    state,
    zip,
    phoneNumber,
    dobMonth,
    dobDay,
    dobYear,
    ssn,
    companyAddress,
    companyCity,
    companyState,
    companyZip,
  } = idVerificationInfo;

  const onInputChange = (e): void => {
    setIdVerificationInfo({
      ...idVerificationInfo,
      [e.currentTarget.dataset.statekey]: {
        value: e.currentTarget.value,
        error: '',
      },
    });
  };

  const onMaskedFieldInputChange = (e): void => {
    setIdVerificationInfo({
      ...idVerificationInfo,
      [e.currentTarget.dataset.statekey]: {
        value: e.currentTarget.value.replace(/[^\d]/g, ''),
        error: '',
      },
    });
  };

  const handleSubmit = async (): Promise<any> => {
    if (!validateAllInputs({ props, state: idVerificationInfo, setIdVerificationInfo })) {
      return;
    }
    let businessFields: Record<string, string> = {
      companyName: '',
      companyDbaName: '',
      companyTaxId: '',
      companyAddress: '',
      companyCity: '',
      companyState: '',
      companyZip: '',
    };
    if (props.selectedOption === 'single_member_llc' || props.selectedOption === 'multi_member_llc') {
      businessFields = {
        ...businessFields,
        companyName: companyName.value.trim(),
        companyDbaName: companyDbaName.value.trim(),
        companyTaxId: companyTaxId.value.trim(),
      };
      if (props.selectedOption === 'multi_member_llc') {
        businessFields = {
          ...businessFields,
          companyAddress: companyAddress.value.trim(),
          companyCity: companyCity.value.trim(),
          companyState: companyState.value,
          companyZip: companyZip.value.trim(),
        };
      }
    }

    setIsLoading(true);
    const response = await props.onSubmit({
      identityVerificationInfo: {
        editMode: false,
        firstName: firstName.value.trim(),
        lastName: lastName.value.trim(),
        address: address.value.trim(),
        city: city.value.trim(),
        state: state.value,
        zip: zip.value.trim(),
        phoneNumber: formatNumber({ country: 'US', phone: phoneNumber.value }, 'NATIONAL'),
        dobMonth: dobMonth.value,
        dobDay: dobDay.value.trim(),
        dobYear: dobYear.value.trim(),
        ssn: ssn.value.trim(),
        ...businessFields,
        selectedTaxVerificationType: props.selectedOption,
      },
    });

    const res = response as { error?: boolean; phoneNumberInvalid?: boolean } | null;
    if (res && res.error) {
      setIsLoading(false);
      setIdVerificationInfo({
        ...idVerificationInfo,
        phoneNumber: res.phoneNumberInvalid
          ? {
              ...phoneNumber,
              error: errorMessages.PHONE_NUMBER_INVALID,
            }
          : { ...phoneNumber },
      });
    } else {
      IreneOnboardingCSEvents.emitSubmitFromTaxVerificationPageEvent({
        currentProperty: props.currentProperty,
        user: props.user,
      });
    }
  };

  const onBack = (): void => {
    props.onBack({
      companyName: companyName.value?.trim(),
      companyDbaName: companyDbaName.value?.trim(),
      companyTaxId: companyTaxId.value?.trim(),
      firstName: firstName.value?.trim(),
      lastName: lastName.value?.trim(),
      address: address.value?.trim(),
      city: city.value?.trim(),
      state: state.value,
      zip: zip.value?.trim(),
      phoneNumber: phoneNumber.value?.trim(),
      dobMonth: dobMonth.value,
      dobDay: dobDay.value?.trim(),
      dobYear: dobYear.value?.trim(),
      ssn: ssn.value?.trim(),
      companyAddress: companyAddress.value?.trim(),
      companyCity: companyCity.value?.trim(),
      companyState: companyState.value,
      companyZip: companyZip.value?.trim(),
    });
  };

  const onCancel = (): void => {
    props.onCancel();
  };

  const renderMonthOptions = (): Array<JSX.Element> | null =>
    months.map((monthOption, index) => (
      <option key={monthOption} value={(index + 1).toString()}>
        {monthOption}
      </option>
    ));

  const renderIndividualTaxForm = () => {
    return (
      <Box
        css={{
          display: 'grid',
          gridTemplateRows: 'auto',
          gap: 'default',
        }}
      >
        <Heading level={3} textStyle="body-lg-bold">
          Verify your individual tax information
        </Heading>

        <Paragraph textStyle="body-sm">Enter your information as it appears on your tax documents.</Paragraph>
        <FormField
          css={{ maxWidth: '36rem' }}
          error={Boolean(firstName.error)}
          required
          control={
            <Input
              fluid
              size="md"
              type="text"
              className="fs-exclude"
              data-statekey="firstName"
              autoComplete="given-name"
              value={firstName.value}
              onChange={onInputChange}
            />
          }
          description={firstName.error && <FormHelp>{firstName.error}</FormHelp>}
          label={<Label>Legal first name</Label>}
        />
        <FormField
          css={{ maxWidth: '36rem' }}
          error={Boolean(lastName.error)}
          required
          control={
            <Input
              fluid
              size="md"
              type="text"
              data-statekey="lastName"
              autoComplete="family-name"
              className="fs-exclude"
              value={lastName.value}
              onChange={onInputChange}
            />
          }
          description={lastName.error && <FormHelp>{lastName.error}</FormHelp>}
          label={<Label>Legal last name</Label>}
        />
        <FormField
          css={{ maxWidth: '36rem' }}
          error={Boolean(address.error)}
          required
          control={
            <Input
              fluid
              size="md"
              data-statekey="address"
              autoComplete="street-address"
              className="fs-exclude"
              value={address.value}
              onChange={onInputChange}
            />
          }
          description={
            address.error ? (
              <FormHelp>{address.error}</FormHelp>
            ) : (
              <FormHelp>Not a post office box or the rental property address</FormHelp>
            )
          }
          label={<Label>Current street address</Label>}
        />

        <Box
          css={{
            display: 'grid',
            maxWidth: '36rem',
            gridTemplateColumns: { base: '1fr', md: '2fr 2fr 1fr' },
            alignItems: 'start',
            gap: 'tighter',
          }}
        >
          <FormField
            css={{ maxWidth: '36rem' }}
            error={Boolean(city.error)}
            required
            control={
              <Input
                fluid
                size="md"
                data-statekey="city"
                autoComplete="address-level2"
                className="fs-exclude"
                value={city.value}
                onChange={onInputChange}
              />
            }
            description={city.error && <FormHelp>{city.error}</FormHelp>}
            label={<Label>City</Label>}
          />
          <FormField
            css={{ maxWidth: '36rem' }}
            error={Boolean(state.error)}
            required
            control={
              <UnitedStatesSelect
                data-statekey="state"
                className="fs-exclude"
                onChange={onInputChange}
                value={state.value}
              />
            }
            description={state.error && <FormHelp>{state.error}</FormHelp>}
            label={<Label>State</Label>}
          />
          <FormField
            css={{ maxWidth: '36rem' }}
            error={Boolean(zip.error)}
            required
            control={
              <Input
                fluid
                size="md"
                data-statekey="zip"
                inputMode="numeric"
                className="fs-exclude"
                value={zip.value}
                onChange={onInputChange}
              />
            }
            description={zip.error && <FormHelp>{zip.error}</FormHelp>}
            label={<Label>ZIP code</Label>}
          />
        </Box>

        <FormField
          css={{ maxWidth: '36rem' }}
          error={Boolean(phoneNumber.error)}
          required
          control={
            <MaskedInput
              statekey="phoneNumber"
              autoComplete="tel"
              className="fs-exclude"
              type="tel"
              value={phoneNumber.value}
              mask="(999) 999-9999"
              onChange={onMaskedFieldInputChange}
            />
          }
          description={phoneNumber.error && <FormHelp>{phoneNumber.error}</FormHelp>}
          label={<Label>Phone number</Label>}
        ></FormField>

        <FieldSet legend={<Legend>Date of birth</Legend>}>
          <Box
            css={{
              display: 'grid',
              maxWidth: '36rem',
              gridTemplateColumns: { base: '1fr', md: '2fr 1fr 1fr' },
              alignItems: 'start',
              gap: 'tighter',
            }}
          >
            <FormField
              css={{ maxWidth: '36rem' }}
              error={Boolean(dobMonth.error)}
              required
              control={
                <Select data-statekey="dobMonth" className="fs-exclude" value={dobMonth.value} onChange={onInputChange}>
                  <option disabled value="">
                    Select one
                  </option>
                  {renderMonthOptions()}
                </Select>
              }
              description={dobMonth.error && <FormHelp>{dobMonth.error}</FormHelp>}
              label={<Label>Month</Label>}
            />
            <FormField
              css={{ maxWidth: '36rem' }}
              error={Boolean(dobDay.error)}
              required
              control={
                <Input
                  className="fs-exclude"
                  data-statekey="dobDay"
                  inputMode="numeric"
                  value={dobDay.value}
                  onChange={onInputChange}
                />
              }
              description={dobDay.error ? <FormHelp>{dobDay.error}</FormHelp> : <FormHelp>DD</FormHelp>}
              label={<Label>Day</Label>}
            />
            <FormField
              css={{ maxWidth: '36rem' }}
              error={Boolean(dobYear.error)}
              required
              control={
                <MaskedInput
                  className="fs-exclude"
                  statekey="dobYear"
                  inputMode="numeric"
                  value={dobYear.value}
                  onChange={onMaskedFieldInputChange}
                  mask="9999"
                />
              }
              description={dobYear.error ? <FormHelp>{dobYear.error}</FormHelp> : <FormHelp>YYYY</FormHelp>}
              label={<Label>Year</Label>}
            />
          </Box>
        </FieldSet>
        <FormField
          css={{ maxWidth: '36rem' }}
          error={Boolean(ssn.error)}
          required
          control={
            <MaskedInput
              className="fs-exclude"
              statekey="ssn"
              inputMode="numeric"
              mask="999-99-9999"
              value={ssn.value}
              onChange={onMaskedFieldInputChange}
            />
          }
          description={
            ssn.error ? (
              <FormHelp>{ssn.error}</FormHelp>
            ) : (
              <FormHelp>Your full Social Security number is needed for tax purposes</FormHelp>
            )
          }
          label={<Label>Social Security number</Label>}
        ></FormField>
      </Box>
    );
  };

  const renderBusinessTaxForm = () => {
    return (
      <Box
        css={{
          display: 'grid',
          gridTemplateRows: 'auto',
          gap: 'default',
        }}
      >
        <Heading level={3} textStyle="body-lg-bold">
          {props.selectedOption === 'single_member_llc'
            ? 'Verify your single LLC tax information'
            : 'Verify your multi-member LLC or corporation tax information'}
        </Heading>

        <Paragraph textStyle="body-sm">Enter the business’s information as it appears on tax documents.</Paragraph>

        <FormField
          css={{ maxWidth: '36rem' }}
          error={Boolean(companyName.error)}
          required
          control={
            <Input
              data-statekey="companyName"
              className="fs-exclude"
              value={companyName.value}
              onChange={onInputChange}
            />
          }
          description={companyName.error && <FormHelp>{companyName.error}</FormHelp>}
          label={<Label>Business name</Label>}
        />

        <FormField
          css={{ maxWidth: '36rem' }}
          error={Boolean(companyDbaName.error)}
          optional
          control={
            <Input
              data-statekey="companyDbaName"
              className="fs-exclude"
              value={companyDbaName.value}
              onChange={onInputChange}
            />
          }
          description={
            companyDbaName.error ? (
              <FormHelp>{companyDbaName.error}</FormHelp>
            ) : (
              <FormHelp>Fill in only if the business operates under a name other than the registered name</FormHelp>
            )
          }
          label={<Label>Doing business as (DBA) name</Label>}
        />
        <FormField
          css={{ maxWidth: '36rem' }}
          error={Boolean(companyTaxId.error)}
          required
          control={
            <MaskedInput
              statekey="companyTaxId"
              inputMode="numeric"
              className="fs-exclude"
              mask="99-9999999"
              value={companyTaxId.value}
              onChange={onMaskedFieldInputChange}
            />
          }
          description={
            companyTaxId.error ? (
              <FormHelp>{companyTaxId.error}</FormHelp>
            ) : (
              <FormHelp>Check your tax documents or business loan applications, or call the IRS</FormHelp>
            )
          }
          label={<Label>Employer identification number (EIN)</Label>}
        />

        {props.selectedOption === 'multi_member_llc' && (
          <>
            <FormField
              css={{ maxWidth: '36rem' }}
              error={Boolean(companyAddress.error)}
              required
              control={
                <Input
                  data-statekey="companyAddress"
                  className="fs-exclude"
                  value={companyAddress.value}
                  onChange={onInputChange}
                />
              }
              description={
                companyAddress.error ? (
                  <FormHelp>{companyAddress.error}</FormHelp>
                ) : (
                  <FormHelp>Not a post office box or the rental property address</FormHelp>
                )
              }
              label={<Label>Business street address</Label>}
            />

            <Box
              css={{
                display: 'grid',
                maxWidth: '36rem',
                gridTemplateColumns: { base: '1fr', md: '2fr 1fr 1fr' },
                alignItems: 'start',
                gap: 'tighter',
              }}
            >
              <FormField
                css={{ maxWidth: '36rem' }}
                error={Boolean(companyCity.error)}
                required
                control={
                  <Input
                    data-statekey="companyCity"
                    className="fs-exclude"
                    value={companyCity.value}
                    onChange={onInputChange}
                  />
                }
                description={companyCity.error && <FormHelp>{companyCity.error}</FormHelp>}
                label={<Label>City</Label>}
              />

              <FormField
                css={{ maxWidth: '36rem' }}
                error={Boolean(companyState.error)}
                required
                control={
                  <UnitedStatesSelect
                    data-statekey="companyState"
                    className="fs-exclude prs"
                    onChange={onInputChange}
                    value={companyState.value}
                  />
                }
                description={companyState.error && <FormHelp>{companyState.error}</FormHelp>}
                label={<Label>State</Label>}
              />
              <FormField
                css={{ maxWidth: '36rem' }}
                error={Boolean(companyZip.error)}
                required
                control={
                  <Input
                    data-statekey="companyZip"
                    inputMode="numeric"
                    className="fs-exclude"
                    value={companyZip.value}
                    onChange={onInputChange}
                  />
                }
                description={companyZip.error && <FormHelp>{companyZip.error}</FormHelp>}
                label={<Label>ZIP code</Label>}
              />
            </Box>
          </>
        )}

        <FieldSet legend={<Legend>Owner information</Legend>}>
          <Box css={{ display: 'grid', gap: 'default' }}>
            <FormField
              css={{ maxWidth: '36rem' }}
              error={Boolean(firstName.error)}
              required
              control={
                <Input
                  data-statekey="firstName"
                  autoComplete="given-name"
                  className="fs-exclude"
                  value={firstName.value}
                  onChange={onInputChange}
                />
              }
              description={firstName.error && <FormHelp>{firstName.error}</FormHelp>}
              label={<Label>First name</Label>}
            />

            <FormField
              css={{ maxWidth: '36rem' }}
              error={Boolean(lastName.error)}
              required
              control={
                <Input
                  data-statekey="lastName"
                  autoComplete="family-name"
                  className="fs-exclude"
                  value={lastName.value}
                  onChange={onInputChange}
                />
              }
              description={lastName.error && <FormHelp>{lastName.error}</FormHelp>}
              label={<Label>Last name</Label>}
            />

            <FormField
              css={{ maxWidth: '36rem' }}
              error={Boolean(address.error)}
              required
              control={
                <Input
                  data-statekey="address"
                  autoComplete="street-address"
                  className="fs-exclude"
                  value={address.value}
                  onChange={onInputChange}
                />
              }
              description={
                address.error ? (
                  <FormHelp>{address.error}</FormHelp>
                ) : (
                  <FormHelp>Not a post office box or the rental property address</FormHelp>
                )
              }
              label={<Label>Street address</Label>}
            />

            <Box
              css={{
                display: 'grid',
                maxWidth: '36rem',
                gridTemplateColumns: { base: '1fr', md: '2fr 1fr 1fr' },
                alignItems: 'start',
                gap: 'tighter',
              }}
            >
              <FormField
                css={{ maxWidth: '36rem' }}
                error={Boolean(city.error)}
                required
                control={
                  <Input
                    data-statekey="city"
                    autoComplete="address-level2"
                    className="fs-exclude"
                    value={city.value}
                    onChange={onInputChange}
                  />
                }
                description={city.error && <FormHelp>{city.error}</FormHelp>}
                label={<Label>City</Label>}
              />

              <FormField
                css={{ maxWidth: '36rem' }}
                error={Boolean(state.error)}
                required
                control={
                  <UnitedStatesSelect
                    data-statekey="state"
                    className="fs-exclude"
                    onChange={onInputChange}
                    value={state.value}
                  />
                }
                description={state.error && <FormHelp>{state.error}</FormHelp>}
                label={<Label>State</Label>}
              />

              <FormField
                css={{ maxWidth: '36rem' }}
                error={Boolean(zip.error)}
                required
                control={
                  <Input
                    data-statekey="zip"
                    inputMode="numeric"
                    className="fs-exclude"
                    value={zip.value}
                    onChange={onInputChange}
                  />
                }
                description={zip.error && <FormHelp>{zip.error}</FormHelp>}
                label={<Label>ZIP code</Label>}
              />
            </Box>

            <FormField
              css={{ maxWidth: '36rem' }}
              error={Boolean(phoneNumber.error)}
              required
              control={
                <MaskedInput
                  statekey="phoneNumber"
                  autoComplete="tel"
                  className="fs-exclude"
                  type="tel"
                  value={phoneNumber.value}
                  mask="(999) 999-9999"
                  onChange={onMaskedFieldInputChange}
                />
              }
              description={phoneNumber.error && <FormHelp>{phoneNumber.error}</FormHelp>}
              label={<Label>Phone number</Label>}
            />

            <FieldSet legend={<Legend>Date of birth</Legend>}>
              <Box
                css={{
                  display: 'grid',
                  maxWidth: '36rem',
                  gridTemplateColumns: { base: '1fr', md: '2fr 1fr 1fr' },
                  alignItems: 'start',
                  gap: 'tighter',
                }}
              >
                <FormField
                  css={{ maxWidth: '36rem' }}
                  error={Boolean(dobMonth.error)}
                  required
                  control={
                    <Select
                      data-statekey="dobMonth"
                      className="fs-exclude"
                      value={dobMonth.value}
                      onChange={onInputChange}
                    >
                      <option disabled value="">
                        Select one
                      </option>
                      {renderMonthOptions()}
                    </Select>
                  }
                  description={dobMonth.error && <FormHelp>{dobMonth.error}</FormHelp>}
                  label={<Label>Month</Label>}
                />

                <FormField
                  css={{ maxWidth: '36rem' }}
                  error={Boolean(dobDay.error)}
                  required
                  control={
                    <Input
                      className="fs-exclude"
                      data-statekey="dobDay"
                      inputMode="numeric"
                      value={dobDay.value}
                      onChange={onInputChange}
                    />
                  }
                  description={dobDay.error ? <FormHelp>{dobDay.error}</FormHelp> : <FormHelp>DD</FormHelp>}
                  label={<Label>Day</Label>}
                />

                <FormField
                  css={{ maxWidth: '36rem' }}
                  error={Boolean(dobYear.error)}
                  required
                  control={
                    <MaskedInput
                      className="fs-exclude"
                      statekey="dobYear"
                      inputMode="numeric"
                      value={dobYear.value}
                      onChange={onMaskedFieldInputChange}
                      mask="9999"
                    />
                  }
                  description={dobYear.error ? <FormHelp>{dobYear.error}</FormHelp> : <FormHelp>YYYY</FormHelp>}
                  label={<Label>Year</Label>}
                />
              </Box>
            </FieldSet>

            <FormField
              css={{ maxWidth: '36rem' }}
              error={Boolean(ssn.error)}
              required
              control={
                <MaskedInput
                  className="fs-exclude"
                  statekey="ssn"
                  inputMode="numeric"
                  mask="999-99-9999"
                  value={ssn.value}
                  onChange={onMaskedFieldInputChange}
                />
              }
              description={
                ssn.error ? (
                  <FormHelp>{ssn.error}</FormHelp>
                ) : (
                  <FormHelp>Your full Social Security number is needed for tax purposes</FormHelp>
                )
              }
              label={<Label>Social Security number</Label>}
            />
          </Box>
        </FieldSet>
      </Box>
    );
  };

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      onRequestClose={props.emergencyMode ? undefined : props.onClose}
      header="Payment setup"
      body={<Box>{props.selectedOption === 'individual' ? renderIndividualTaxForm() : renderBusinessTaxForm()}</Box>}
      backAction={{ onClick: onBack }}
      footerAriaLabel="Tax information actions"
      footerEnd={
        <>
          {props.editMode && <TextButton onClick={onCancel}>Cancel</TextButton>}
          <Button tone="brand" emphasis="filled" onClick={handleSubmit} loading={isLoading}>
            {props.editMode ? 'Update' : 'Continue'}
          </Button>
        </>
      }
    />
  );
};

TaxInfoInputPage.defaultProps = {
  companyName: '',
  companyDbaName: '',
  companyTaxId: '',
  firstName: '',
  lastName: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  phoneNumber: '',
  verifiedPhoneNumber: '',
  dobMonth: '',
  dobDay: '',
  dobYear: '',
  ssn: '',
  editMode: false,
  companyAddress: '',
  companyCity: '',
  companyState: '',
  companyZip: '',
};

export default TaxInfoInputPage;
